import React, { useCallback, useEffect } from "react";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { EditableReviewItem } from "../../../common/components/EditableReviewItem";
import SSNMask from "../../../common/components/mask/SSNMask";
import MobileNumberMask from "../../../common/components/mask/MobileNumberMask";
import AddressZipMask from "../../../common/components/mask/AddressZipMask";
import { gql, useMutation } from "@apollo/client";
import clientGraphql from "../../../../common/apollo-graphql";
import Swal from "sweetalert2";

const PatientInfoForm = ({ details, edit, referralUuid, setIsEditMode, refetch, setSaving }) => {
  const { control, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    defaultValues: {...details},
  });

  const SAVE_PATIENT_MUTATION = gql`
    mutation ($params: String!) {
      save_patient(params: $params)
    }
  `;

  const [savePatient] = useMutation(SAVE_PATIENT_MUTATION, {
    client: clientGraphql,
  });

  const getLabel = (custom) => custom.label;
  const getInputProps = useCallback(fieldName => {
    if (fieldName === 'ssn') {
      return {
        inputComponent: SSNMask,
      };
    } else if (fieldName === 'phone') {
      return { inputComponent: MobileNumberMask };
    } else if (fieldName === 'address_zip') {
      return {
        inputComponent: AddressZipMask,
      };
    } else {
      return null;
    }
  }, []);

  const handleRetrieve = (address) => setValue("full_address", address);

  const onSubmit = (data) => {
    setSaving(true);
    const renderedFields = [
      "id",
      "firstname",
      "lastname",
      "phone",
      "ssn",
      "mrn",
      "birthdate",
      "full_address",
    ];

    const filteredData = Object.entries(data)
      .filter(([key, value]) => renderedFields.includes(key) && value !== "" && value != null)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

    filteredData.referral_uuid = referralUuid;
    filteredData.is_public_patient = !data['ssn'];

    savePatient({
      variables: {
        params: JSON.stringify(filteredData),
      },
    }).then(() => {
      setIsEditMode(false);
      refetch();
      Swal.fire({
        icon: 'success',
        text: "Saved Patient Information.",
        showConfirmButton: true,
      })
    }).catch((error) => {
      console.error(error);
      Swal.fire("Failed to save patient information.");
    });
  };

  useEffect(() => {
    if (details) {
      reset(details);
    }
  }, [details, reset]);

  return (
    <form id="patient-info-form" onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        {[
          { field: "firstname", label: "First Name", kind: "text" },
          { field: "lastname", label: "Last Name", kind: "text" },
          { field: "phone", label: "Phone", kind: "phone" },
          { field: "ssn", label: "SSN", kind: "text" },
          { field: "mrn", label: "MRN", kind: "text" },
          { field: "birthdate", label: "Birthdate", kind: "date" },
          { field: "full_address", label: "Address", kind: "address", handleRetrieve },
        ]
          .map(({ field, label, kind, handleRetrieve }) => (
            <EditableReviewItem
              key={field}
              edit={edit}
              custom={{ field, label, kind }}
              control={control}
              errors={errors}
              getLabel={getLabel}
              getInputProps={getInputProps}
              label={label}
              value={details[field]}
              handleRetrieve={handleRetrieve}
            />
          ))}
      </Grid>

      {edit && <button type="submit" style={{ display: "none" }} />}
    </form>
  );

};

export default PatientInfoForm;
